import { runInAction } from "mobx";
import { isNil } from "lodash";

import { useStore } from "@vromo/mobx-react/mobx-react";
import { useToasts } from "@vromo/ui/components/Toasts";
import { RootStore } from "../../stores/RootStore";
import { Platform } from "@vromo/models/integrations";
import { IntegrationApiLocationAction } from "@vromo/api/Integration";
import { useAccount } from "../../account";
import { Tracking } from '@vromo/analytics/tracking';
import { useIntegrationsSession } from "./useIntegrationsSession";
import { useIntegrationsPage } from "./useIntegrationsPage";




/** Leverage Integrations Page MOBx state context and common functions
 * @note requires container component to be mobx "observer"
 */
export const useIntegrationLocations = () => {


    const showToast = useToasts();
    const { createIntegrationToken } = useAccount()
    const {
        setLoading,
        integrationsApi,
        fetchedIntegrations,
    } = useIntegrationsPage()

    const { retryWithNewToken } = useIntegrationsSession()
    const { authStore: { role: { group }, user: { contact } } } = useStore<RootStore>('rootStore');
    const email = contact?.email ?? "nobody@email.com"



    /** create a new location for a particular integration platform  
     * @note currenlty only creates a location
    */
    const createLocations = async (platform: Platform, data: any) => {
        try {
            setLoading(true)
            runInAction(() => fetchedIntegrations.get(platform)!.status = "synching")
            const vromoAccessToken = platform === Platform.uberEatsKH ?
                await createIntegrationToken(group!.id!, `${platform}-integration-app`)! :
                undefined
            const { result: newIntegrationLocation } = await retryWithNewToken(() =>
                integrationsApi.createLocations(platform)({
                    body: {
                        ...vromoAccessToken && { vromo_access_token: vromoAccessToken },
                        ...data
                    }
                })
            )
            Tracking.action(`group '${group!.id!}' ${platform} integration location '${newIntegrationLocation.ext_site_id}' was created by user '${email}'`);
            fetchedIntegrations.get(platform)!.ext_locations?.push(newIntegrationLocation)
            showToast(`New ${platform} integration location was created`, "success")
            fetchedIntegrations.has(platform) && runInAction(() => fetchedIntegrations.get(platform)!.status = undefined)
        }
        catch (err) {
            Tracking.error(err)
            showToast(`Could not integration with the target new ${platform} location`, "error")
            runInAction(() => fetchedIntegrations.get(platform)!.status = "error")
        }
        finally {
            setLoading(false)
        }
    }

    /** fetch, list locations, or enable / disable a given integration location */
    const queryLocations = async (platform: Platform, action: IntegrationApiLocationAction, locationId?: string) => {
        try {
            setLoading(true)
            runInAction(() => fetchedIntegrations.get(platform)!.status = "synching")

            if (["enable", "disable"].includes(action)) {
                await retryWithNewToken(() =>
                    integrationsApi.toogleLocation(fetchedIntegrations.get(platform)!.features!.locations!)({
                        body: { action, id: locationId! }
                    })
                )
                Tracking.action(`group '${group!.id!}' ${platform} integration location '${locationId}' was ${action} by user '${email}'`);
                const updatedLocation = (fetchedIntegrations.get(platform)!.ext_locations ?? []).find((location) => location.ext_site_id === locationId)
                !isNil(updatedLocation) && runInAction(() => updatedLocation.enabled = !updatedLocation.enabled)
            }
            else {
                const { result } = await retryWithNewToken(() =>
                    integrationsApi.queryLocations(fetchedIntegrations.get(platform)!.features!.locations!)({ query: { action } })
                )
                Tracking.action(`${platform} integration locations were ${action} by user '${email}' (group '${group!.id!}')`);
                runInAction(() => fetchedIntegrations.get(platform)!.ext_locations = result)
            }
        }
        catch (err) {
            Tracking.error(err)
            fetchedIntegrations.has(platform) && runInAction(() => fetchedIntegrations.get(platform)!.ext_locations = [])
            showToast(`Could not complete the '${action}' location(s) action for ${platform} integration`, "error")
        }
        finally {
            fetchedIntegrations.has(platform) && runInAction(() => fetchedIntegrations.get(platform)!.status = undefined)
            setLoading(false)
        }
    }




    return {
        queryLocations,
        createLocations
    }

}